import {
  useContext,
  useState,
  ReactNode,
  createContext,
  useEffect,
} from "react";

import io from "socket.io-client";

import { WorkNotification } from "interfaces/Notification";
import { baseURL } from "../services/api";

import validatorPhoto from "assets/audio/alert.mp3";
import IsLateAlert from "assets/audio/isLate.mp3";
import arrivedAlert from "assets/audio/policemSiren.mp3";
import { useAuth } from "./authContext";
import { socket } from "hooks/useSocket";
import { SocketAddress } from "net";

type ProviderValues = {
  children: ReactNode;
};

type ContextValues = {
  notifications: WorkNotification[];
  hasUnseenNotification: () => boolean;
  seeNotification: (notification: WorkNotification) => void;
  seeAllNotifications: () => void;
};

socket.on("connect", () => {
  console.log("Notifications are now online.");
});

export const NotificationContext = createContext({} as ContextValues);

type AudioTypes = "isLate" | "arrived" | "validator"

export function NotificationProvider({ children }: ProviderValues) {
  const { isAuthenticated, tenant } = useAuth();
  const [notifications, setNotifications] = useState<WorkNotification[]>([]);

  useEffect(() => {
    Notification.requestPermission();

    if (isAuthenticated && tenant) {
      socket.on(`notify-${tenant.id}`, formatNotification);
      socket.on("PolicemanIsLate", () => playAlarm("isLate"));
      socket.on("PolicemanArrived", () => playAlarm("arrived"));
      socket.on("PolicemanValidatorPhoto", () => playAlarm("validator"));
    }

    return () => {
      if (isAuthenticated && tenant) {
        socket.off(`notify-${tenant.id}`);
        socket.off("PolicemanIsLate");
        socket.off("PolicemanArrived");
        socket.off("PolicemanValidatorPhoto");
      }
    };
  }, [isAuthenticated, tenant]);

  function playAlarm(type: AudioTypes) {
    const audioSounds = {
      isLate: IsLateAlert,
      arrived: arrivedAlert,
      validator: validatorPhoto,
    }
    
    const audio = new Audio(audioSounds[type])
    
    audio.play() 

  }

  function formatNotification(notification: WorkNotification) {
    notification.saw = false;

    setNotifications((list) => [notification, ...list]);

    if (Notification.permission === "granted") {
      new Notification("PRMA Federal", {
        body: notification.message,
        silent: true,
        icon: "/favicon.png",
      });
    }
  }

  function hasUnseenNotification(): boolean {
    const unseenNotification = notifications.find(
      (notif) => notif.saw === false
    );

    if (!unseenNotification) {
      return false;
    } else {
      return true;
    }
  }

  function seeNotification(notification: WorkNotification) {
    const notificationList = [...notifications];

    const index = notificationList.indexOf(notification);
    notificationList[index].saw = true;

    setNotifications(notificationList);
  }

  function seeAllNotifications() {
    const notificationList = notifications.map((notification) => {
      notification.saw = true;

      return notification;
    });

    setNotifications(notificationList);
  }

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        hasUnseenNotification,
        seeNotification,
        seeAllNotifications,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export const useNotifications = () => {
  return useContext(NotificationContext);
};
